import React from 'react';
import { Cell, Row } from 'react-table';
import { FormattedMessage } from 'react-intl';
import Highlighter from 'react-highlight-words';
import UserLogo from '../../../../components/UserLogo';
import { formatDate } from '../../../../common/helpers/dateHelper';
import CustomSelect from '../../../../components/CustomSelect';
import { RoleType } from '../../../../common/enums/RoleType';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IUserStatus } from '../../../../common/models/user/IUserStatus';
import { IUserInfo } from '../../../../common/models/user/IUserInfo';
import { IUserRole } from '../../../../common/models/user/IUserRole';
import TeamPopup from '../TeamPopup';
import { getRoleNodes } from '../../../../common/helpers/nodesHelper';
import LinkButton from '../../../../components/LinkButton';
import Icon from '../../../../components/Icon';
import { Column } from '../../../../common/models/table/Column';
import styles from './styles.module.scss';

export const getColumns = (
  search: string,
  currentUserId: string,
  onChangeRole: IBindingCallback1<IUserRole>,
  onChangeStatus: IBindingCallback1<IUserStatus>,
  resendInvite: IBindingCallback1<string>
): Column[] => [
  {
    Header: () => <FormattedMessage id="usersPane.name" defaultMessage="User name" />,
    id: 'name',
    width: 230,
    Cell: ({ row: { original } }) => {
      const { firstName, lastName, email } = original as IUserInfo;
      return (
        <div className="d-flex align-items-center">
          <UserLogo user={original as IUserInfo} />
          <div className="flex-grow-1 ml-4 d-flex flex-column overflow-ellipsis">
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[search]}
              textToHighlight={`${firstName} ${lastName}`}
              className="overflow-ellipsis"
              autoEscape
            />
            <Highlighter
              highlightClassName="highlighted-text"
              searchWords={[search]}
              textToHighlight={email}
              className={`overflow-ellipsis ${styles.userEmail}`}
              autoEscape
            />
          </div>
        </div>
      );
    }
  },
  {
    Header: () => <FormattedMessage id="role" defaultMessage="Role" />,
    id: 'role',
    accessor: 'role',
    Cell: ({ row: { original } }) => {
      const { role, id, isActive } = original as IUserInfo;
      return role === RoleType.SuperAdmin
        ? <span>Super Admin</span>
        : (
          <div className={styles.roleTrigger}>
            <CustomSelect
              value={role}
              onChange={val => onChangeRole({ id, role: val as RoleType })}
              options={getRoleNodes()}
              disabled={id === currentUserId || !isActive}
            />
          </div>
        );
    }
  },
  {
    Header: () => <FormattedMessage id="team" defaultMessage="Team" />,
    id: 'team',
    accessor: 'team',
    width: 240,
    Cell: ({ row: { original } }) => {
      const { team, id, isActive } = original as IUserInfo;
      return <TeamPopup disabled={!isActive} userId={id} selectedTeam={team} />;
    }
  },
  {
    Header: () => <FormattedMessage id="usersPane.lastLogin" defaultMessage="Last login" />,
    accessor: 'lastLoginDate',
    Cell: ({ row: { original }, cell: { value } }: { row: Row<IUserInfo>; cell: Cell<Date> }) => (
      (value ? formatDate(value) : (
        <LinkButton
          onClick={() => resendInvite(original.email)}
          disabled={!original.isActive}
          textIntlId="usersPane.resendInvite"
          defaultMessage="Resend invite"
          icon={<Icon icon="reminder" />}
        />
      ))
    )
  },
  {
    id: 'isActive',
    accessor: 'isActive',
    width: 95,
    Cell: ({ row: { original } }) => {
      const { isActive, id, role } = original as IUserInfo;
      return (
        <LinkButton
          disabled={role === RoleType.SuperAdmin || id === currentUserId}
          onClick={() => onChangeStatus({ id, isActive: !isActive })}
          textIntlId={isActive ? 'usersPane.deactivate' : 'usersPane.reactivate'}
          defaultMessage={isActive ? 'Deactivate' : 'Reactivate'}
        />
      );
    }
  }
];
